import { apiSyncVisit } from '@api/syncVisit';
import type { Response } from '@models/response';
import type { ExamInvitationResponse } from '@models/syncVisit';
import { useQuizStore, useUserStore } from '@stores/index';
import { ref } from 'vue';
import { useToast } from 'vue-toast-notification';

const MAX_INVITE_RETRIES = 5;
const INITIAL_RETRY_DELAY_MS = 2000;
const HTTP_TOO_EARLY = 425;

type FetchError = Error & {
  getStatus?: () => number | null
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useQualiphy = () => {
  const userStore = useUserStore();
  const quizStore = useQuizStore();
  const toast = useToast();

  const isQualiphyAvailable = ref(false);
  const meetingUrl = ref('');
  const getQualiphyLoading = ref(false);

  const getInviteWithRetry = async (): Promise<Response<ExamInvitationResponse> | null> => {
    const patientId = userStore.info?.id;

    if (!patientId || !quizStore.submissionId) {
      return null;
    }

    const payload = {
      treatmentId: null,
      patientId,
      submissionId: quizStore.submissionId,
      sendEmail: true,
    };

    for (let attempt = 0; attempt <= MAX_INVITE_RETRIES; attempt += 1) {
      try {
        return await apiSyncVisit.getInvitationData({ ...payload, attempt });
      } catch (error) {
        const fetchError = error as FetchError;
        const status = fetchError.getStatus?.();

        if (status !== HTTP_TOO_EARLY) {
          toast.error(fetchError.message || 'Something went wrong. Please try again later.');

          return null;
        }

        if (attempt === MAX_INVITE_RETRIES) {
          toast.error(
            'Your provider review is still being prepared. Please refresh the page or try again later.',
          );

          return null;
        }

        await delay(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));
      }
    }

    return null;
  };

  const getQualiphy = async () => {
    try {
      getQualiphyLoading.value = true;

      const [availabilityResponse, inviteResponse] = await Promise.all([
        apiSyncVisit.isQualifyAvailable().catch(() => null),
        getInviteWithRetry(),
      ]);

      isQualiphyAvailable.value = availabilityResponse?.data?.available ?? false;
      meetingUrl.value = inviteResponse?.data?.meeting_url ?? '';
    } finally {
      getQualiphyLoading.value = false;
    }
  };

  return {
    getQualiphy,
    getQualiphyLoading,
    isQualiphyAvailable,
    meetingUrl,
  };
};
